import {
  cn,
  Flex,
  TabGroup,
  TabGroupList,
  TabGroupTrigger,
} from "@100mslive/hms-ai-ui";
import { Table2Columns } from "iconoir-react";
import { isObject } from "lodash-es";
import moment from "moment";
import React, { useState } from "react";

import { fromUTC } from "../../../utils/time";

type Props = {
  data: Record<string, any>;
  scrollStyle?: Record<string, any>;
};

const formatBookedSlot = (dateTimeStr: string): string => {
  const date = new Date(dateTimeStr);

  if (isNaN(date.getTime())) {
    return dateTimeStr;
  }

  const formatter = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  // Get all parts and build custom string without commas or "at"
  const parts = formatter.formatToParts(date);
  let output = "";

  parts.forEach(part => {
    switch (part.type) {
      case "literal":
        // Add a single space between parts, skip commas and "at"
        if (part.value !== ", " && part.value !== " at ") {
          output += part.value;
        } else {
          output += " ";
        }
        break;
      default:
        output += part.value;
    }
  });

  return output;
};

const formatDateTime = (dateStr: string) => {
  try {
    const parts = dateStr.match(/^(\w+)\s+(\w+)\s+(\d+)\s+(\d+:\d+\s+[AP]M)$/);
    if (!parts) {
      return dateStr;
    }

    const [, weekday, month, day, time] = parts;

    return `${weekday}, ${month} ${day}, ${time}`;
  } catch (error) {
    console.error("Error parsing date/time:", { error, dateStr });
    return dateStr;
  }
};

const getFormattedValue = (value: any, key: string): string => {
  if (key === "dob") {
    const date = moment(value);
    return date.isValid() ? date.format("MM-DD-YYYY") : String(value);
  }
  if (["booked_slots", "future", "past", "available"].includes(key)) {
    value = value?.map((slot: string) => formatBookedSlot(slot));
  }
  if (Array.isArray(value)) {
    return value?.map(item => formatDateTime(item)).join("\n");
  }

  if (typeof value === "string" && value.length > 0) {
    return value[0].toUpperCase() + value.slice(1);
  }

  if (isObject(value)) {
    return JSON.stringify(value, null, 2);
  }
  if (value === null || value === undefined) {
    return "-";
  }
  return value?.toString();
};

const renderEmptyState = () => {
  return (
    <Flex
      className="w-full h-full items-center justify-center flex-col"
      style={{ height: "400px" }}
    >
      <Flex className="border border-bd-secondary rounded-lg p-2 mb-4">
        <Table2Columns className="w-5 h-5" />
      </Flex>
      <p className="text-txt-secondary text-sm font-semibold">
        No data available
      </p>
    </Flex>
  );
};

export const ContextHeader = ({ data, scrollStyle }: Props) => {
  const { flags, slots, extra, ...otherFields } = data.input_data ?? {};
  const { disposition, output_data = {}, input_data = {} } = data ?? {};
  let convertedRequestedTime = "";
  if (disposition?.["requested_time"] && output_data?.["timezone"]) {
    convertedRequestedTime = fromUTC(
      disposition["requested_time"],
      output_data["timezone"],
    );
  }

  const finalData = { ...disposition, ...output_data };
  if (convertedRequestedTime) {
    finalData["requested_time"] = convertedRequestedTime;
  }
  const [activeTab, setActiveTab] = useState("output");

  const renderOutputData = () => {
    const isEmpty = Object.keys(finalData).length === 0;
    if (isEmpty) {
      return renderEmptyState();
    }
    return (
      <div>
        {Object.entries(finalData)
          .sort(([, a], [, b]) => {
            const isANull = a === null || a === undefined;
            const isBNull = b === null || b === undefined;
            if (isANull === isBNull) {return 0;}
            return isANull ? 1 : -1;
          })
          .map(([key, value], index) => (
            <LabelCard
              key={`${key}_${index}`}
              value={getFormattedValue(value, key)}
              label={key}
            />
          ))}
      </div>
    );
  };

  const renderInputData = () => {
    const isEmpty = Object.keys(input_data).length === 0;
    if (isEmpty) {
      return renderEmptyState();
    }
    return (
      <div>
        {Object.entries({
          ...slots,
          ...extra,
          ...flags,
          ...otherFields,
        })?.map(([key, value], index) => {
          return value ? (
            <LabelCard
              key={`${key}_${index}`}
              value={getFormattedValue(value, key)}
              label={key}
            />
          ) : (
            ""
          );
        })}
      </div>
    );
  };

  return (
    <Flex
      direction="column"
      className="border-bd-primary border p-1.5 bg-bg-secondary-alt rounded-xl w-full overflow-y-auto"
      style={{ height: "400px", ...scrollStyle }}
    >
      <TabGroup
        defaultValue="output"
        className="w-full"
        onValueChange={value => setActiveTab(value)}
      >
        <TabGroupList className="border-none">
          <TabGroupTrigger
            value="output"
            className={cn(
              "rounded-lg font-semibold py-2",
              activeTab === "output"
                ? "text-txt-secondary"
                : "text-txt-quaternary border border-transparent",
            )}
          >
            Output Data
          </TabGroupTrigger>
          <TabGroupTrigger
            value="input"
            className={cn(
              "rounded-lg text-txt-secondary font-semibold py-2",
              activeTab === "input"
                ? "text-txt-secondary"
                : "text-txt-quaternary border border-transparent",
            )}
          >
            Input Data
          </TabGroupTrigger>
        </TabGroupList>
      </TabGroup>
      <div className="overflow-y-auto">
        {activeTab === "output" ? renderOutputData() : renderInputData()}
      </div>
    </Flex>
  );
};

const LabelCard = ({ value, label }: { value: string; label: string }) => {
  return (
    <Flex
      direction="column"
      className="px-4 py-3 last:border-b-0 border-b border-bd-primary"
    >
      <p className="font-normal text-txt-quaternary text-sm capitalize">
        {label.replaceAll("_", " ")}
      </p>
      <p className="font-medium text-txt-secondary text-sm whitespace-pre-line">
        {value}
      </p>
    </Flex>
  );
};
